/**
 * src/api/alerts.ts
 * ------------------
 * API functions for the Alerts module.
 *
 * Endpoints used:
 *   GET    /alerts                       → paginated alert list (filter by status/severity)
 *   GET    /alerts/:id                   → alert detail
 *   POST   /alerts/:id/acknowledge       → mark alert as acknowledged
 *   POST   /alerts/:id/resolve           → mark alert as resolved
 *   DELETE /alerts/:id                   → delete alert record
 *
 *   GET    /alert-rules                  → list configured rules
 *   POST   /alert-rules                  → create new rule
 *   PUT    /alert-rules/:id              → update rule
 *   DELETE /alert-rules/:id              → delete rule
 *   POST   /alert-rules/:id/toggle       → enable/disable rule
 *   POST   /alert-rules/test-email       → send test escalation email
 *
 * Depends on: src/api/client.ts, shared/types (Alert, AlertRule, AlertSeverity)
 */
import apiClient from './client'
import type { Alert, AlertRule, AlertSeverity, AlertStatus, PaginatedResponse, PaginationParams } from '@shared/types'

export interface ListAlertsParams extends PaginationParams {
  status?: AlertStatus
  severity?: AlertSeverity
  from?: string    // ISO date string
  to?: string
}

export interface CreateAlertRulePayload {
  name: string
  condition: string
  severity: AlertSeverity
  escalation_emails: string[]
  cooldown_minutes: number
}

// ── Alerts ────────────────────────────────────────────────────────────────────

export async function listAlerts(params?: ListAlertsParams): Promise<PaginatedResponse<Alert>> {
  const { data } = await apiClient.get<PaginatedResponse<Alert>>('/alerts/events', { params })
  return data
}

export async function getAlert(id: string): Promise<Alert> {
  const { data } = await apiClient.get<Alert>(`/alerts/${id}`)
  return data
}

export async function acknowledgeAlert(id: string): Promise<Alert> {
  const { data } = await apiClient.post<Alert>(`/alerts/${id}/acknowledge`)
  return data
}

export async function resolveAlert(id: string): Promise<Alert> {
  const { data } = await apiClient.post<Alert>(`/alerts/${id}/resolve`)
  return data
}

export async function deleteAlert(id: string): Promise<void> {
  await apiClient.delete(`/alerts/${id}`)
}

export async function getAlertsUnreadCount(): Promise<number> {
  const { data } = await apiClient.get<{ count: number }>('/alerts/unread-count')
  return data.count
}

export async function markAlertsRead(): Promise<number> {
  const { data } = await apiClient.post<{ marked: number }>('/alerts/events/read')
  return data.marked
}

// ── Alert Rules ───────────────────────────────────────────────────────────────

export async function listAlertRules(): Promise<AlertRule[]> {
  const { data } = await apiClient.get<AlertRule[]>('/alerts/rules')
  return data
}

export async function createAlertRule(payload: CreateAlertRulePayload): Promise<AlertRule> {
  const { data } = await apiClient.post<AlertRule>('/alerts/rules', payload)
  return data
}

export async function updateAlertRule(
  id: string,
  payload: Partial<CreateAlertRulePayload>,
): Promise<AlertRule> {
  const { data } = await apiClient.put<AlertRule>(`/alerts/rules/${id}`, payload)
  return data
}

export async function deleteAlertRule(id: string): Promise<void> {
  await apiClient.delete(`/alerts/rules/${id}`)
}

export async function toggleAlertRule(id: string, enabled: boolean): Promise<AlertRule> {
  const { data } = await apiClient.post<AlertRule>(`/alerts/rules/${id}/toggle`, { enabled })
  return data
}

export async function testAlertRule(id: string): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>(`/alerts/rules/${id}/test`)
  return data
}
